import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useHistory, useLocation } from 'react-router'

import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import {
  getVoucherDisplayConfigs,
  type VoucherDisplayConfigVoucher,
} from 'api/promo'
import VoucherDisplayModel from 'marketing/components/VoucherDisplayModel'
import { matchUrlPath } from './shared/utils'

const VOUCHER_POPUP_QUERY_PARAM = 'vp'
const VOUCHER_POPUP_FORCE_QUERY_PARAM = 'vpf'
const VOUCHERS_PAGE_PATH = '/account/vouchers'
const POPUP_DELAY_MS = 500
const DISMISSED_STORAGE_KEY = 'le-convert-voucher-dismissed'

function isVoucherDismissed(key: string): boolean {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY)
    if (!stored) return false
    return (JSON.parse(stored) as Array<string>).includes(key)
  } catch {
    return false
  }
}

function persistVoucherDismissal(key: string): void {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY)
    const dismissed: Array<string> = stored ? JSON.parse(stored) : []
    if (!dismissed.includes(key)) {
      dismissed.push(key)
      localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(dismissed))
    }
  } catch {
    // ignore storage errors (private browsing, quota exceeded)
  }
}

function normalizeValue(value: string | undefined | null): string | undefined {
  const normalized = value?.trim().toLowerCase()
  return normalized && normalized.length > 0 ? normalized : undefined
}

function voucherMatchesValue(
  voucher: Pick<VoucherDisplayConfigVoucher, 'voucherName' | 'voucherTitle'>,
  value: string,
): boolean {
  return normalizeValue(voucher.voucherName) === value ||
    normalizeValue(voucher.voucherTitle) === value
}

function VoucherPopupPrompt() {
  const { search, pathname } = useLocation()
  const history = useHistory()
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const utmCampaign = useAppSelector(state => state.utm.campaign)
  const shownKeyRef = useRef<string | null>(null)
  const promptRef = useRef<HTMLDivElement | null>(null)
  const [show, setShow] = useState(false)
  const isEnabled = config.ENABLE_VOUCHER_DISPLAY_CONFIG

  const pathnameWithoutRegion = useMemo(
    () => pathname.replace(/^\/[a-z]{2}\//i, '/'),
    [pathname],
  )

  const { vpValue, vpfValue } = useMemo(() => {
    const params = new URLSearchParams(search)
    return {
      vpValue: normalizeValue(params.get(VOUCHER_POPUP_QUERY_PARAM)),
      vpfValue: normalizeValue(params.get(VOUCHER_POPUP_FORCE_QUERY_PARAM)),
    }
  }, [search])

  const paramValue = vpValue ?? vpfValue
  const isForced = !vpValue && Boolean(vpfValue)
  const shouldFetch = isEnabled && Boolean(paramValue)

  const { data: displayConfigs = [] } = useQuery({
    queryKey: ['voucher-display-configs', region],
    queryFn: () => getVoucherDisplayConfigs({ region }),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  })

  const match: VoucherDisplayConfigVoucher | undefined = useMemo(() => {
    if (!paramValue) return undefined
    for (const cfg of displayConfigs) {
      if (!isForced) {
        const urlMatch = cfg.urlPaths.some((pattern: string) => matchUrlPath(pattern, pathnameWithoutRegion))
        if (!urlMatch) continue
        if (cfg.source && cfg.source !== utmSource) continue
        if (cfg.medium && cfg.medium !== utmMedium) continue
        if (cfg.campaign && cfg.campaign !== utmCampaign) continue
      }
      const voucher = cfg.vouchers.find(candidate => voucherMatchesValue(candidate, paramValue))
      if (voucher) return voucher
    }
    return undefined
  }, [displayConfigs, isForced, paramValue, pathnameWithoutRegion, utmCampaign, utmMedium, utmSource])

  useEffect(() => {
    if (!match || !paramValue) return
    const matchKey = `${match.voucherName}:${paramValue}`
    if (shownKeyRef.current === matchKey) return
    if (isVoucherDismissed(matchKey)) return
    const timer = window.setTimeout(() => {
      shownKeyRef.current = matchKey
      setShow(true)
    }, POPUP_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [match, paramValue])

  const handleClose = useCallback(() => setShow(false), [])

  const handleDismiss = useCallback(() => {
    if (match && paramValue) {
      persistVoucherDismissal(`${match.voucherName}:${paramValue}`)
    }
    setShow(false)
  }, [match, paramValue])

  const handleViewVouchers = useCallback(() => {
    setShow(false)
    history.push(VOUCHERS_PAGE_PATH)
  }, [history])

  if (!match) return null

  return (
    <VoucherDisplayModel
      show={show}
      promptRef={promptRef}
      voucher={match}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onViewVouchers={handleViewVouchers}
    />
  )
}

export default VoucherPopupPrompt
